/* eslint-disable i18next/no-literal-string */
import { pageDataService } from 'common/utilities/PageDataService';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { AgeGroup, UserItpSummary } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';

import {
  CodeVerificationStatus,
  IFamilyMember,
  IFamilyMemberBreaches,
  IItpUserContext,
  IOneTimeScan,
  IUnauthOneTimeScan,
  SSNVerificationStatus,
  SubscriberProfile,
} from './identityMonitoring.types';

/**
 * The selected user who is being onboarded (current user or family member).
 * This data comes from IDSAPI.
 */
export interface ISelectedUserOnboarding {
  dateOfBirth: string | undefined;
  dateOfBirthApiStatus: ApiCallResultStatus;
  ageGroup: AgeGroup | undefined;
}

/**
 * Identity monitoring profile and cyber data of current user and family members
 */
export interface IIdentityMonitoringState {
  // ITP user context that should be used for ITP components and hooks
  userContext: IItpUserContext;

  // members
  familyMembersLoadingStatus: ApiCallResultStatus;
  familyMembersLoadError: string | undefined;
  familyMembers: IFamilyMember[];

  oneTimeScan: IOneTimeScan;
  unauthOneTimeScan: IUnauthOneTimeScan;
}

export const createDefaultMemberBreachState = (): IFamilyMemberBreaches => ({
  breaches: [],
  getBreachesApiStatus: ApiCallResultStatus.DEFAULT,
});

export const createDefaultMemberSubscriberProfileState = (): SubscriberProfile => ({
  isSSNAdded: false,
  isSSNVerified: false,
  cyberData: {},
  unverifiedContactInfo: {},
  msaData: {
    emailAddresses: { dataType: 'EmailAddress', values: [] },
    telephoneNumbers: { dataType: 'TelephoneNumber', values: [] },
  },
});

export const createDefaultFamilyMember = (userSummary: UserItpSummary): IFamilyMember => ({
  getCompleteSubscriberProfileApiStatus: ApiCallResultStatus.DEFAULT,
  subscriberProfile: createDefaultMemberSubscriberProfileState(),
  primaryEmailAddress: undefined,
  deleteSubscriptionApiStatus: undefined,
  emailCodeVerificationStatus: CodeVerificationStatus.DEFAULT,
  phoneCodeVerificationStatus: CodeVerificationStatus.DEFAULT,
  ssnVerification: { status: SSNVerificationStatus.DEFAULT },
  ...createDefaultMemberBreachState(),
  ...userSummary,

  // credit summary
  creditSummary: undefined,
  getCreditSummaryApiStatus: ApiCallResultStatus.DEFAULT,
});

export const createDefaultSelectedUserOnboarding = (): ISelectedUserOnboarding => ({
  dateOfBirth: undefined,
  dateOfBirthApiStatus: ApiCallResultStatus.DEFAULT,
  ageGroup: undefined,
});

export function getDefaultIdentityMonitoringState(): IIdentityMonitoringState {
  const userData = pageDataService.getUserData();
  let { csid, displayName } = userData;
  let userContextUserId: string | undefined = userData.userId;
  let isCurrentUser = true;

  // initialize the family state with current user based on page data
  const familyMembers: IFamilyMember[] = [
    createDefaultFamilyMember({
      csid,
      name: displayName,
      userId: userData.userId,
      itpOnboardingStatus: pageDataService.isITPOnboarded() ? 'Monitoring' : 'Unmonitored',
      cmOnboardingStatus: pageDataService.isCMOnboarded() ? 'Monitoring' : 'Unmonitored',
      ssnValidationStatus: pageDataService.getSSNValidationStatus() || 'NotAvailable',
      userInfo: { isCurrentUser: true, userId: userData.userId, displayName },
    }),
  ];

  // handle csid override via query param
  // Scenario 1: opening webview dialogs with csid
  // Scenario 2: opening a recommendation dialog from ITP email notification link with csid
  const query = new URLSearchParams(window.location.search);
  const csidOverride = query.get('csid');
  const nameOverride = query.get('name');

  if (csidOverride) {
    isCurrentUser = localeIgnoreCaseEquals(csidOverride, csid);

    if (!isCurrentUser) {
      csid = csidOverride;
      userContextUserId = undefined;
      displayName = nameOverride || '';
      familyMembers.push(
        createDefaultFamilyMember({
          csid,
          userId: undefined,
          name: displayName,
          userInfo: { isCurrentUser: false, displayName },
        })
      );
    }
  }

  return {
    userContext: {
      csid,
      userId: userContextUserId,
      isCurrentUser,
      displayName,
    },
    familyMembersLoadingStatus: ApiCallResultStatus.DEFAULT,
    familyMembersLoadError: undefined,
    familyMembers,
    oneTimeScan: {
      statusData: undefined,
      breaches: [],
      statusDataApiStatus: ApiCallResultStatus.DEFAULT,
      emailCodeVerificationStatus: CodeVerificationStatus.DEFAULT,
      scanAndGetBreachesApiStatus: ApiCallResultStatus.DEFAULT,
      getExistingBreachesApiStatus: ApiCallResultStatus.DEFAULT,
    },
    unauthOneTimeScan: {
      statusData: undefined,
      breaches: [],
      sessionId: undefined,
      statusDataApiStatus: ApiCallResultStatus.DEFAULT,
      emailCodeVerificationStatus: CodeVerificationStatus.DEFAULT,
      scanAndGetBreachesApiStatus: ApiCallResultStatus.DEFAULT,
    },
  };
}
